import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  Plus, 
  Trash2, 
  Star, 
  User, 
  MapPin, 
  MessageSquare,
  Check,
  X,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    text: '',
    location: '',
    rating: 5,
    image_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(response.data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);

    try {
      await axios.post(`${API_URL}/api/testimonials`, {
        name: formData.name,
        text: formData.text,
        location: formData.location || null,
        rating: formData.rating,
        image_url: formData.image_url || null
      });
      
      setMessage({ type: 'success', text: 'Testimonial adăugat!' });
      setFormData({ name: '', text: '', location: '', rating: 5, image_url: '' });
      setShowForm(false);
      fetchTestimonials();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la adăugarea testimonialului.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi acest testimonial?')) return;

    try {
      await axios.delete(`${API_URL}/api/testimonials/${id}`);
      setMessage({ type: 'success', text: 'Testimonial șters!' });
      fetchTestimonials();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la ștergerea testimonialului.' });
    }
  };

  const handleSeedTestimonials = async () => {
    try {
      await axios.post(`${API_URL}/api/testimonials/seed`);
      setMessage({ type: 'success', text: 'Testimoniale demo adăugate!' });
      fetchTestimonials();
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la adăugarea testimonialelor.' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Testimoniale</h1>
          <p className="text-slate-400 mt-1">{testimonials.length} testimoniale active</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSeedTestimonials}
            className="rounded-xl border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Demo
          </Button>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adaugă
          </Button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
            : 'bg-red-500/20 text-red-400 border border-red-500/50'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Add Form */}
      {showForm && (
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
          <h2 className="text-xl font-bold text-white mb-4">Testimonial Nou</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Nume *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="ex: Maria D."
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Locație
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="ex: București, România"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                <MessageSquare className="w-4 h-4 inline mr-1" />
                Testimonial *
              </label>
              <textarea
                value={formData.text}
                onChange={(e) => setFormData({...formData, text: e.target.value})}
                required
                rows={3}
                placeholder="Scrie testimonialul aici..."
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  <Star className="w-4 h-4 inline mr-1" />
                  Rating
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({...formData, rating: star})}
                      className="focus:outline-none"
                    >
                      <Star 
                        className={`w-6 h-6 ${star <= formData.rating ? 'text-amber-400 fill-current' : 'text-slate-600'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  URL Imagine (opțional)
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={submitting} className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl">
                {submitting ? 'Se salvează...' : 'Salvează'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-xl border-slate-600 text-slate-300 hover:bg-slate-700">
                Anulează
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Testimonials List */}
      <div className="space-y-4">
        {testimonials.length === 0 ? (
          <div className="text-center py-12 bg-slate-800 rounded-2xl border border-slate-700">
            <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Nu există testimoniale.</p>
            <p className="text-slate-500 text-sm mt-1">Adaugă primul testimonial sau apasă "Demo".</p>
          </div>
        ) : (
          testimonials.map((testimonial) => (
            <div 
              key={testimonial.id}
              className="bg-slate-800 rounded-2xl p-6 border border-slate-700 flex items-start justify-between gap-4"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {testimonial.image_url ? (
                    <img 
                      src={testimonial.image_url} 
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-sky-600 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    {testimonial.location && (
                      <p className="text-slate-400 text-sm">{testimonial.location}</p>
                    )}
                  </div>
                  <div className="flex gap-0.5 ml-auto">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-slate-300 text-sm">"{testimonial.text}"</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => handleDelete(testimonial.id)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      {/* Info */}
      <div className="p-4 bg-sky-500/10 border border-sky-500/30 rounded-xl">
        <p className="text-sky-300 text-sm">
          <strong>Notă:</strong> Testimonialele adăugate aici apar automat pe pagina principală (maxim 4).
        </p>
      </div>
    </div>
  );
};

export default AdminTestimonials;
